import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, ErrorText, PageHeader, Pagination, Select, Spinner } from "../components/ui.tsx";
import { keepPreviousData, usePaging } from "../hooks.ts";
import { EventBadge, fmtBytes, fmtUsd, PhaseBadge, UsageBar } from "../components/runtime.tsx";

export function RuntimesPage() {
  const qc = useQueryClient();
  const data = useQuery({ queryKey: ["runtimes"], queryFn: () => unwrap(api.admin.runtimes.$get()), refetchInterval: 10_000 });
  const [filter, setFilter] = useState("");
  const paging = usePaging([filter]);
  const { page, pageSize } = paging;
  const events = useQuery({
    queryKey: ["runtime-events", filter, page, pageSize],
    queryFn: () => unwrap(api.admin.runtimes.events.$get({ query: { page: String(page), pageSize: String(pageSize), ...(filter && { type: filter }) } })),
    refetchInterval: 15_000,
    placeholderData: keepPreviousData,
  });
  const eventTotal = events.data?.total;
  useEffect(() => void paging.clamp(eventTotal), [eventTotal]); // eslint-disable-line react-hooks/exhaustive-deps
  const act = useMutation({
    mutationFn: ({ userId, action }: { userId: string; action: "start" | "stop" | "restart" }) =>
      unwrap(api.admin.runtimes[":userId"][":action{start|stop|restart}"].$post({ param: { userId, action } })),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["runtimes"] });
      void qc.invalidateQueries({ queryKey: ["runtime-events"] });
    },
  });
  const restartAll = useMutation({
    mutationFn: () => unwrap(api.admin.runtimes["restart-all"].$post()),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["runtimes"] }),
  });

  const rows = data.data?.runtimes ?? [];
  const used = rows.reduce((a, r) => a + (r.storageUsedBytes ?? 0), 0);
  const cap = rows.reduce((a, r) => a + (r.storageCapacityBytes ?? 0), 0);
  const spend = rows.reduce((a, r) => a + r.spendUsd, 0);
  const ooms = rows.reduce((a, r) => a + r.oomsLast7d, 0);

  return (
    <>
      <PageHeader
        title="Assistants"
        description="Every user runs their own assistant in an isolated container with its own disk and memory limit."
        actions={
          <Button disabled={!data.data?.enabled || restartAll.isPending || !rows.length} onClick={() => confirm("Restart every running assistant?") && restartAll.mutate()}>
            Restart all
          </Button>
        }
      />
      {data.data && !data.data.enabled && (
        <Card className="mb-6 border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">Per-user assistants are turned off on this deployment.</Card>
      )}
      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <Kpi label="Running" value={`${rows.filter((r) => r.phase === "running").length} / ${rows.length}`} />
        <Kpi label="Storage used" value={fmtBytes(used)} sub={`of ${fmtBytes(cap)} allocated`} />
        <Kpi label="LLM spend this month" value={fmtUsd(spend)} />
        <Kpi label="Out-of-memory kills (7 days)" value={String(ooms)} bad={ooms > 0} />
      </div>
      <ErrorText error={act.error ?? restartAll.error} />
      <Card className="mt-3 overflow-x-auto">
        {data.isPending ? (
          <div className="grid place-items-center py-16"><Spinner /></div>
        ) : rows.length === 0 ? (
          <p className="px-6 py-12 text-center text-sm text-[var(--muted)]">No assistants yet. A user's assistant is created the first time they sign in.</p>
        ) : (
          <table className="w-full min-w-[800px] text-sm">
            <thead>
              <tr className="border-b border-[var(--line)] bg-[var(--panel-2)]/40 text-left text-[12.5px] font-medium text-[var(--muted)]">
                <th className="px-3 py-3 font-medium">User</th>
                <th className="px-3 py-3 font-medium">Status</th>
                <th className="px-3 py-3 font-medium">Memory</th>
                <th className="px-3 py-3 font-medium">Storage</th>
                <th className="px-3 py-3 font-medium">Spend (mo)</th>
                <th className="px-3 py-3 font-medium">OOM (7d)</th>
                <th className="px-3 py-3" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.userId} className="border-b border-[var(--line)] last:border-0">
                  <td className="px-3 py-3">
                    <div className="font-medium">{r.name}</div>
                    <div className="text-xs text-[var(--muted)]">{r.email} · {r.groupName ?? "No group"}</div>
                  </td>
                  <td className="px-3 py-3">
                    <PhaseBadge phase={r.phase} />
                    {r.message && <div className="mt-1 max-w-36 truncate text-xs text-[var(--muted)]" title={r.message}>{r.message}</div>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs text-[var(--muted)]">
                    {r.quotas ? <>Agent {r.quotas.memoryMb} MB<br />Sandbox {r.quotas.sandboxMemoryMb} MB</> : "Group defaults"}
                  </td>
                  <td className="px-3 py-3"><UsageBar used={r.storageUsedBytes} total={r.storageCapacityBytes} /></td>
                  <td className="px-3 py-3">{fmtUsd(r.spendUsd)}</td>
                  <td className="px-3 py-3">{r.oomsLast7d ? <Badge tone="bad">{r.oomsLast7d}</Badge> : <span className="text-[var(--muted)]">0</span>}</td>
                  <td className="px-3 py-3">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" className="h-8 px-2.5" disabled={act.isPending} onClick={() => act.mutate({ userId: r.userId, action: "restart" })}>Restart</Button>
                      {r.desired === "running" ? (
                        <Button variant="ghost" className="h-8 px-2.5" disabled={act.isPending} onClick={() => confirm(`Stop ${r.email}'s assistant?`) && act.mutate({ userId: r.userId, action: "stop" })}>Stop</Button>
                      ) : (
                        <Button variant="ghost" className="h-8 px-2.5" disabled={act.isPending} onClick={() => act.mutate({ userId: r.userId, action: "start" })}>Start</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      <div className="mt-10 mb-3 flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Activity</h2>
        <Select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-8 w-48">
          <option value="">All events</option>
          <option value="oom">Out of memory</option>
          <option value="error">Errors</option>
          <option value="restarted">Restarts</option>
          <option value="resized">Storage resizes</option>
        </Select>
      </div>
      <Card className="overflow-hidden">
        <ErrorText error={events.error} />
        {events.isPending && <div className="grid place-items-center py-10"><Spinner /></div>}
        {events.data?.rows.length === 0 && <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">Nothing yet.</p>}
        <div className={`divide-y divide-[var(--line)] ${events.isPlaceholderData ? "opacity-60" : ""}`}>
          {events.data?.rows.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-5 py-3 text-sm">
              <span className="w-40 shrink-0 text-xs text-[var(--muted)]">{new Date(e.at).toLocaleString()}</span>
              <EventBadge type={e.type} />
              <span className="min-w-0 flex-1">{e.message}</span>
              <span className="text-xs text-[var(--muted)]">{e.email}</span>
            </div>
          ))}
        </div>
        <Pagination page={page} pageSize={pageSize} total={eventTotal ?? 0} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={events.isFetching} />
      </Card>
    </>
  );
}

function Kpi({ label, value, sub, bad }: { label: string; value: string; sub?: string; bad?: boolean }) {
  return (
    <Card className="p-4">
      <div className="text-xs text-[var(--muted)]">{label}</div>
      <div className={`mt-2 text-[24px] leading-none font-semibold tracking-[-0.02em] ${bad ? "text-red-700" : ""}`}>{value}</div>
      {sub && <div className="mt-1.5 text-xs text-[var(--muted)]">{sub}</div>}
    </Card>
  );
}
